import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { FeaturedVideo } from '@/shared/types';
import { ButtonLink } from '@/shared/ui';
import { safePlay } from '@/shared/utils/media';
import { cssUrl } from '@/shared/utils/cssUrl';

export type LandingStageProps = {
  /** Every showcase entry, in curator order. The stage cycles through them. */
  videos: FeaturedVideo[];
  /**
   * Whether a signed-in viewer is looking at this.
   *
   * Only the copy and the CTA differ: a visitor is being asked to sign up, and a
   * signed-in viewer already has. The reel, the cycling and the credit line are
   * identical, which is the point -- one component, so the two surfaces cannot
   * drift the way two copies of it would.
   */
  signedIn?: boolean;
};

/**
 * The full-bleed opening stage: the showcase reel playing behind the promise.
 *
 * This is the one place on either surface where video plays without being asked
 * for, and it earns that by being wallpaper rather than content — muted, no
 * controls, `aria-hidden`, and carrying no information the copy in front of it
 * does not already say. A visitor who arrives, reads two lines and leaves has
 * still seen what the product makes, which is the whole job of the page. The
 * catalogue below stays click-to-play, exactly like Home.
 *
 * It advances on `ended` rather than on a timer: a fixed interval cuts a long
 * clip mid-shot and sits on a short one after it has already stopped, and the
 * durations here are curator-controlled, not known to this component. One clip
 * still loops, because "advance to the next" and "there is no next" want the
 * same visual result — continuous motion — from different mechanisms.
 *
 * `muted` is not decoration: every browser blocks autoplay with sound, so the
 * unmuted version of this is a still frame that never moves. `playsInline` is
 * what stops iOS opening it fullscreen over the page.
 */
export function LandingStage({ videos, signedIn = false }: LandingStageProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [index, setIndex] = useState(0);

  /* Clamped rather than trusted. The list is refetched on "Try again", and a
     shorter one arriving while the reel sat on its last entry would otherwise
     index past the end and blank the stage. */
  const safeIndex = videos.length > 0 ? index % videos.length : 0;
  const video = videos[safeIndex];
  const single = videos.length <= 1;

  // Play on every change of entry, not just on mount. The autoplay attribute
  // alone loses races on a slow connection (the element can be ready before its
  // source is), and a poster with no motion behind it reads as a broken player.
  // safePlay is the same helper Home's hero uses, so a rejected play() promise is
  // swallowed the same way here.
  useEffect(() => {
    safePlay(videoRef.current);
  }, [video?.id]);

  if (!video) return null;

  return (
    <section
      className="stage"
      aria-labelledby="stage-title"
      /* The poster travels as a custom property so the reduced-motion rule can
         paint it as a background once the <video> is hidden. CSS cannot reach a
         `poster` attribute, and duplicating the URL in a stylesheet is not
         possible when it is signed and per-entry. */
      style={{ '--stage-poster': video.thumbnailUrl ? cssUrl(video.thumbnailUrl) : 'none' } as CSSProperties}
    >
      <div className="stage__media" aria-hidden>
        <video
          ref={videoRef}
          /* Keyed on the entry so a source change remounts the element rather
             than asking one player to swap `src` mid-playback — the latter keeps
             the outgoing frame on screen until the new one decodes. */
          key={video.id}
          className="stage__video"
          src={video.videoUrl}
          poster={video.thumbnailUrl}
          muted
          /* Loop only when it is the whole reel; otherwise `ended` has to fire
             for the next entry to get its turn, and a looping video never ends. */
          loop={single}
          playsInline
          preload="metadata"
          tabIndex={-1}
          onEnded={single ? undefined : () => setIndex((i) => (i + 1) % videos.length)}
          /* A single unplayable entry would otherwise freeze the reel on a black
             rectangle for good. Treating an error like an ending steps past it. */
          onError={single ? undefined : () => setIndex((i) => (i + 1) % videos.length)}
        />
        <div className="stage__scrim" />
      </div>

      <div className="stage__body">
        {/* The eyebrow carries the first clause of the supplied subheadline rather
            than repeating it: "Literature and imagination, now accessible for all"
            opened the lead, and the eyebrow already said almost exactly that two
            lines above. One statement of it, in the slot built for a short one. */}
        <p className="stage__eyebrow">
          {/* The eyebrow is the only visible copy signed in, so it carries the whole
              job of saying what this is. */}
          {signedIn ? 'Featured' : 'Literature and imagination, accessible for all'}
        </p>
        {/* Signed in this is a real, visible heading again -- stripping it left the
            stage blank, which read as an unfinished page rather than a restrained one.
            What it is NOT is the signed-out pitch: "Watch What You Read" is asking a
            stranger to sign up, and a viewer who already has an account has heard it.
            Two words that name the page instead.

            It keeps its id either way: the stage's `aria-labelledby` points here and
            this is the document's only h1. */}
        <h1 className="stage__title" id="stage-title">
          {signedIn ? (
            /* No `<em>` on the brand name, unlike the signed-out headline where the
               italic falls on "Read" as emphasis. `.stage__title em` is an italic, and
               italicising half a two-word phrase made "Made on" and "Valoi" read as two
               different fonts. A wordmark is also the one word on the page that should
               never be restyled for rhythm. */
            'Made on Valoi'
          ) : (
            <>
              Watch What You <em>Read</em>
            </>
          )}
        </h1>
        {/* One line, deliberately. This ran to four over moving footage -- six claims
            competing with the video they were describing -- and nobody reads a
            paragraph on a landing page. The headline is the claim now; this is the
            specifics, and the specifics that survived are the ones the rest of the
            product cannot show: what goes in, and that the output is watchable,
            annotatable and kept. */}
        {/* One line on both, but a different job. Signed out it sells the idea; signed
            in it says what the shelf is, so the page is not a bare video with a title
            floating over it.

            Not "Now Showing" for the headline, which this briefly was: the hero
            directly below carries a NOW SHOWING label of its own, so the two stacked
            up as the same words twice in one screen. */}
        <p className="stage__lead">
          {signedIn
            ? 'Every video we have rendered, free to watch.'
            : 'Novels, textbooks, your own writing — turned into video you can watch, annotate, and keep.'}
        </p>
        {/* No CTA over the video signed in. It is a real action rather than a pitch,
            so it is not wrong here -- but with the headline and lead gone it was a lone
            green button floating over someone's film, and the rail already carries New
            content two inches to the left. The page's closing section still offers it,
            which is the right place for a next step: after the catalogue, not on top of
            it. */}
        {signedIn ? null : (
          <ButtonLink to="/login" variant="primary" size="lg" className="stage__cta">
            Start Generating
          </ButtonLink>
        )}
        {/* Names whatever is playing behind the copy, so an arresting frame is
            attributable to a real title in the catalogue below rather than
            looking like stock footage. `aria-live` because the text changes on
            its own: without it a screen reader announces the first title and
            then silently goes stale. */}
        <p className="stage__credit" aria-live="polite">
          Now playing <span className="stage__credit-title">{video.title}</span>
        </p>
      </div>
    </section>
  );
}
